import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Input, Spin, Table } from "antd";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { getConnectedAnalyticsUsers } from "../../../apis/analyticsUsageApi";
import AnalyticsHelpIcon from "./AnalyticsHelpIcon";

const AnalyticsConnectedUsers = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0 });
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getConnectedAnalyticsUsers({
        page,
        limit: pagination.limit,
        source: "identities",
        search: search || undefined,
      });
      setRows(res?.result?.rows || []);
      setPagination((prev) => ({
        ...prev,
        page,
        total: res?.result?.pagination?.total || 0,
        total_pages: res?.result?.pagination?.total_pages || 0,
      }));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load connected users");
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, search]);

  useEffect(() => {
    load(1);
  }, [load]);

  const columns = [
    {
      title: "User",
      key: "user",
      render: (_, row) => {
        const u = row.user || {};
        const name = [u.first_name, u.last_name].filter(Boolean).join(" ") || "—";
        return (
          <div>
            <strong>{name}</strong>
            <div className="analytics-user-meta">{u.email || "—"}</div>
          </div>
        );
      },
    },
    {
      title: "PostHog ID",
      key: "posthog",
      render: (_, row) => (
        <code className="analytics-code-sm">
          {row.analytics_identity?.posthog_distinct_id || "—"}
        </code>
      ),
    },
    {
      title: "Attribution",
      key: "attr",
      render: (_, row) => {
        const id = row.analytics_identity || {};
        return (
          <div>
            <div>{id.af_media_source || "—"}</div>
            <small className="analytics-user-meta">{id.af_campaign || ""}</small>
          </div>
        );
      },
    },
    {
      title: "Last sync",
      key: "sync",
      width: 130,
      render: (_, row) => {
        const ts = row.analytics_identity?.client_synced_at || row.analytics_identity?.updated_at;
        return ts ? dayjs(ts).format("D MMM YYYY") : "—";
      },
    },
    {
      title: "",
      key: "action",
      width: 120,
      render: (_, row) =>
        row.user?.id ? (
          <Link to={`/analytics-usage/users/${row.user.id}`} className="analytics-view-more">
            View activity <i className="fa-solid fa-arrow-right" />
          </Link>
        ) : null,
    },
  ];

  return (
    <div className="analytics-dashboard analytics-connected-users">
      <header className="analytics-dashboard__header">
        <div>
          <h1>
            Connected Users
            <AnalyticsHelpIcon helpKey="connected-users" />
          </h1>
          <p>Users synced from inv-fe with PostHog / AppsFlyer identity mappings</p>
        </div>
        <div className="analytics-dashboard__controls">
          <Input.Search
            placeholder="Search name, email, PostHog ID…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={(v) => setSearch(v.trim())}
            allowClear
            style={{ width: 280 }}
          />
          <Button onClick={() => load(pagination.page)} loading={loading}>
            Refresh
          </Button>
        </div>
      </header>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} showIcon />}

      <div className="analytics-panel">
        {loading && !rows.length ? (
          <div className="analytics-dashboard__loading">
            <Spin size="large" tip="Loading connected users..." />
          </div>
        ) : (
          <Table
            size="small"
            className="analytics-table"
            columns={columns}
            dataSource={rows.map((r, i) => ({ ...r, key: r.user?.id || i }))}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              showSizeChanger: false,
              onChange: (p) => load(p),
            }}
            locale={{ emptyText: "No connected users with PostHog identity found" }}
          />
        )}
      </div>
    </div>
  );
};

export default AnalyticsConnectedUsers;
